import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useAuth } from '../auth/AuthContext'
import { dashboardService, DashboardContextResponse, PeriodPreset, getDefaultDashboardPeriod } from './dashboardService'
import { TeamTrackingView } from './TeamTrackingView'
import { TeamSportPerformanceView } from './TeamSportPerformanceView'
import { IndividualPlayerPerformanceView } from './IndividualPlayerPerformanceView'
import {
  Users,
  Zap,
  User,
  Calendar,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Shield,
  Layers,
} from 'lucide-react'

interface DashboardViewProps {
  onNavigateToEvaluations?: () => void
  onNavigateToHealth?: () => void
  onNavigateToStats?: () => void
  initialExperience?: 'tracking' | 'sport' | 'individual'
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  onNavigateToEvaluations,
  onNavigateToHealth,
  onNavigateToStats,
  initialExperience = 'tracking',
}) => {
  const { user } = useAuth()
  const [activeExperience, setActiveExperience] = useState<'tracking' | 'sport' | 'individual'>(
    initialExperience
  )
  const [context, setContext] = useState<DashboardContextResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [errorDetail, setErrorDetail] = useState<string | null>(null)

  // Equipos autorizados
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([])
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')

  // Filtros de concentración y convocatoria
  const [selectedCamp, setSelectedCamp] = useState<string>('')
  const [campScope, setCampScope] = useState<'convened' | 'all_active'>('convened')
  const [showInactive, setShowInactive] = useState<boolean>(false)

  // Selector de periodo (Últimas 8 semanas | Intervalo concentración | Todo el histórico | Personalizado)
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('last_8_weeks')
  const [customFrom, setCustomFrom] = useState<string>('2026-05-01')
  const [customTo, setCustomTo] = useState<string>(new Date().toISOString().slice(0, 10))

  // Referencia para evitar condiciones de carrera en peticiones asíncronas
  const activeRequestId = useRef(0)

  // Identificar rol y capacidades
  const isAthlete = Boolean(
    user?.memberships.some(
      (m) => m.roles.includes('athlete') && !m.roles.includes('coach') && !m.roles.includes('medic') && !m.roles.includes('admin')
    )
  )
  const isCoach = Boolean(user?.memberships.some((m) => m.roles.includes('coach')))

  // En caso de sesión de atleta, forzar siempre la experiencia exclusiva 'individual' (Mi Rendimiento)
  useEffect(() => {
    if (isAthlete && activeExperience !== 'individual') {
      setActiveExperience('individual')
    }
  }, [isAthlete, activeExperience])

  // Concentración actual seleccionada (si procede)
  const currentCampObj = context?.camps?.find((c) => c.id === selectedCamp)

  // Periodo efectivo calculado según el ajuste seleccionado
  const effectivePeriod = useMemo(() => {
    return getDefaultDashboardPeriod(periodPreset, {
      customFrom,
      customTo,
      campFrom: currentCampObj?.start_time,
      campTo: currentCampObj?.end_time,
    })
  }, [periodPreset, customFrom, customTo, currentCampObj])

  // 1. Cargar equipos autorizados para el usuario
  useEffect(() => {
    let isMounted = true
    async function initTeams() {
      try {
        const availableTeams = await dashboardService.getTeams()
        if (!isMounted) return
        setTeams(availableTeams)

        if (availableTeams.length > 0) {
          const userTeam = user?.memberships.find((m) => m.team_id)?.team_id
          const match = availableTeams.find((t) => t.id === userTeam)
          setSelectedTeamId(match ? match.id : availableTeams[0].id)
        } else {
          const fallback = user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'
          setSelectedTeamId(fallback)
        }
      } catch (err) {
        if (!isMounted) return
        console.warn('Error resolviendo equipos del dashboard:', err)
        const fallback = user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'
        setSelectedTeamId(fallback)
      }
    }

    initTeams()
    return () => {
      isMounted = false
    }
  }, [user])

  // Población efectiva vs Actividad efectiva:
  // - Población: solo filtra la lista de atletas si se pide 'convened'. Si es 'all_active', la población es todo el equipo.
  const populationCamp = selectedCamp && campScope === 'convened' ? selectedCamp : undefined
  // - Actividad: si hay concentración seleccionada, los partidos, snaps y evaluaciones se filtran por ella.
  const activityCampId = selectedCamp || undefined

  // 2. Cargar contexto operativo según filtros y periodo efectivo
  const loadContext = async () => {
    if (!selectedTeamId) return

    const reqId = ++activeRequestId.current
    setLoading(true)
    setError(null)
    setErrorDetail(null)

    try {
      const data = await dashboardService.getContext({
        teamId: selectedTeamId,
        from: effectivePeriod.from,
        to: effectivePeriod.to,
        camp: populationCamp,
        inactive: showInactive,
      })

      if (activeRequestId.current !== reqId) return
      setContext(data)
    } catch (err: unknown) {
      if (activeRequestId.current !== reqId) return
      console.warn('Error cargando contexto del dashboard:', err)
      const msg = err instanceof Error ? err.message : String(err)
      setError('No se pudo cargar el contexto operativo del equipo.')
      setErrorDetail(msg)
    } finally {
      if (activeRequestId.current === reqId) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (selectedTeamId) {
      loadContext()
    }
  }, [selectedTeamId, selectedCamp, campScope, showInactive, effectivePeriod.from, effectivePeriod.to])

  // Al seleccionar una concentración, si el periodo está en 'last_8_weeks', ofrecer o conmutar al intervalo de la concentración si es anterior
  const handleCampChange = (newCampId: string) => {
    setSelectedCamp(newCampId)
    if (newCampId) {
      const camp = context?.camps?.find((c) => c.id === newCampId)
      if (camp?.start_time) {
        const campDate = camp.start_time.slice(0, 10)
        const eightWeeksAgo = getDefaultDashboardPeriod('last_8_weeks').from
        // Si la concentración fue antes de las últimas 8 semanas, cambiar automáticamente a intervalo de concentración
        if (campDate < eightWeeksAgo) {
          setPeriodPreset('camp_interval')
        }
      }
    } else {
      if (periodPreset === 'camp_interval') {
        setPeriodPreset('last_8_weeks')
      }
    }
  }

  return (
    <div className="container" style={{ padding: '1rem 0.75rem', maxWidth: '1440px', margin: '0 auto', boxSizing: 'border-box', width: '100%' }}>
      {/* Header institucional */}
      <div
        style={{
          background: 'var(--nav-bg-base, #0a1320)',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          color: '#ffffff',
          marginBottom: '1.25rem',
          boxShadow: '0 4px 12px rgba(10, 19, 32, 0.15)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span
              style={{
                backgroundColor: 'var(--brand-red, #b91c1c)',
                color: '#ffffff',
                fontSize: '0.68rem',
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {isAthlete ? 'Mi Rendimiento · Datos Personales' : 'M8C Operativo · Datos Reales'}
            </span>
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
              Selección Española de Flag Football 5v5
            </span>
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: '#ffffff',
            }}
          >
            {isAthlete ? 'Mi Rendimiento Deportivo · Atleta FEFA' : 'Dashboard Integrado de Seguimiento y Rendimiento'}
          </h1>
        </div>

        {/* Selector de experiencia y enlace a preview (Solo para staff/coach; los atletas tienen vista exclusiva) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.65rem' }}>
          {!isAthlete ? (
            <>
              <div
                style={{
                  display: 'inline-flex',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '3px',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveExperience('tracking')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: activeExperience === 'tracking' ? '#ffffff' : 'transparent',
                    color: activeExperience === 'tracking' ? '#0a1320' : '#ffffff',
                    fontSize: '0.78rem',
                    fontWeight: activeExperience === 'tracking' ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Users size={14} />
                  <span>Seguimiento del Equipo</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveExperience('sport')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: activeExperience === 'sport' ? '#ffffff' : 'transparent',
                    color: activeExperience === 'sport' ? '#0a1320' : '#ffffff',
                    fontSize: '0.78rem',
                    fontWeight: activeExperience === 'sport' ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Zap size={14} />
                  <span>Rendimiento Deportivo</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveExperience('individual')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: activeExperience === 'individual' ? '#ffffff' : 'transparent',
                    color: activeExperience === 'individual' ? '#0a1320' : '#ffffff',
                    fontSize: '0.78rem',
                    fontWeight: activeExperience === 'individual' ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <User size={14} />
                  <span>Rendimiento Individual</span>
                </button>
              </div>

              {onNavigateToEvaluations && (
                <button
                  type="button"
                  onClick={onNavigateToEvaluations}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid var(--nav-border, #1a2d4a)',
                    color: '#ffffff',
                    fontSize: '0.74rem',
                    cursor: 'pointer',
                  }}
                >
                  <span>Registrar Evaluación M8B</span>
                </button>
              )}

              <a
                href="/?view=dashboard-preview"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.72rem',
                  color: 'rgba(255,255,255,0.7)',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  textDecoration: 'none',
                }}
                title="Ver propuesta visual con datos sintéticos aislados"
              >
                <span>Propuesta Sintética</span>
                <ExternalLink size={12} />
              </a>
            </>
          ) : (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '0.78rem',
                color: '#ffffff',
              }}
            >
              <User size={15} color="#ffffff" />
              <span style={{ fontWeight: 700 }}>Mi Rendimiento</span>
              <span style={{ opacity: 0.7 }}>· Espacio personal del atleta</span>
            </div>
          )}
        </div>
      </div>

      {/* Barra de filtros de contexto */}
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          fontSize: '0.8rem',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', maxWidth: '100%', minWidth: 0 }}>
          {/* Selector de equipo (solo staff con más de un equipo; los atletas pertenecen a su equipo federativo) */}
          {!isAthlete && teams.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', maxWidth: '100%', minWidth: 0 }}>
              <Shield size={15} color="var(--text-muted, #64748b)" />
              <span style={{ fontWeight: 600, color: 'var(--text-main, #0f172a)' }}>Equipo:</span>
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                style={{
                  fontSize: '0.78rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  border: '1px solid var(--surface-border, #cbd5e1)',
                  background: 'var(--surface-subtle, #f8fafc)',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                }}
              >
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Filtro de concentración */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
            <Calendar size={15} color="var(--text-muted, #64748b)" />
            <span style={{ fontWeight: 600, color: 'var(--text-main, #0f172a)' }}>Concentración:</span>
            <select
              data-testid="camp-select"
              aria-label="Concentración"
              value={selectedCamp}
              onChange={(e) => handleCampChange(e.target.value)}
              style={{
                fontSize: '0.78rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid var(--surface-border, #cbd5e1)',
                background: 'var(--surface-subtle, #f8fafc)',
                maxWidth: 'min(100%, 280px)',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              <option value="">Todas las concentraciones del ciclo</option>
              {context?.camps?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          {/* Distinción clara de ámbito cuando hay concentración seleccionada (solo para staff) */}
          {!isAthlete && selectedCamp !== '' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
              <Layers size={14} color="var(--text-muted, #64748b)" />
              <span style={{ fontWeight: 600, color: 'var(--text-main, #0f172a)' }}>Población:</span>
              <select
                data-testid="scope-select"
                aria-label="Población"
                value={campScope}
                onChange={(e) => setCampScope(e.target.value as 'convened' | 'all_active')}
                style={{
                  fontSize: '0.78rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  border: '1px solid var(--surface-border, #cbd5e1)',
                  background: 'var(--surface-subtle, #f8fafc)',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                }}
              >
                <option value="convened">Solo convocados a esta concentración</option>
                <option value="all_active">Todos los jugadores en seguimiento activo</option>
              </select>
            </div>
          )}

          {/* Selector de periodo coherente entre KPIs, gráficos y drill-downs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-main, #0f172a)' }}>Periodo:</span>
            <select
              data-testid="period-select"
              aria-label="Periodo"
              value={periodPreset}
              onChange={(e) => setPeriodPreset(e.target.value as PeriodPreset)}
              style={{
                fontSize: '0.78rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid var(--surface-border, #cbd5e1)',
                background: 'var(--surface-subtle, #f8fafc)',
                maxWidth: 'min(100%, 260px)',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              <option value="last_8_weeks">Últimas 8 semanas</option>
              {selectedCamp !== '' && currentCampObj?.start_time && (
                <option value="camp_interval">Intervalo de la concentración</option>
              )}
              <option value="all_history">Todo el histórico (Temporada 2026)</option>
              <option value="custom">Periodo personalizado</option>
            </select>

            {periodPreset === 'custom' && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.2rem 0.4rem',
                    borderRadius: '4px',
                    border: '1px solid var(--surface-border, #cbd5e1)',
                    maxWidth: '125px',
                  }}
                  title="Fecha inicio"
                />
                <span style={{ color: 'var(--text-muted, #64748b)' }}>a</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.2rem 0.4rem',
                    borderRadius: '4px',
                    border: '1px solid var(--surface-border, #cbd5e1)',
                    maxWidth: '125px',
                  }}
                  title="Fecha fin"
                />
              </div>
            )}
          </div>

          {/* Periodo efectivo siempre visible */}
          <div
            data-testid="effective-period-badge"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 8px',
              borderRadius: '6px',
              background: 'rgba(10, 19, 32, 0.05)',
              border: '1px solid var(--surface-border, #cbd5e1)',
              color: 'var(--text-main, #0f172a)',
              fontSize: '0.75rem',
              flexWrap: 'wrap',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Calendar size={13} color="var(--brand-blue, #1d4ed8)" />
            <span>
              Periodo efectivo: <strong>{effectivePeriod.from}</strong> al <strong>{effectivePeriod.to}</strong>
            </span>
          </div>

          {/* Opción independiente de seguimiento inactivo / archivados (solo staff autorizado) */}
          {!isAthlete && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                cursor: 'pointer',
                color: 'var(--text-secondary, #475569)',
              }}
            >
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span>Incluir atletas pausados o archivados</span>
            </label>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #64748b)' }}>
            {context?.athletes ? `${context.athletes.length} atletas en censo` : ''}
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={loadContext}
            disabled={loading}
            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
            title="Recargar datos del dashboard"
          >
            <RefreshCw size={13} className={loading ? 'spin' : ''} />
            <span>Actualizar</span>
          </button>
        </div>

        {/* Alcance desacoplado de actividad vs población cuando hay concentración seleccionada */}
        {selectedCamp !== '' && (
          <div
            style={{
              width: '100%',
              padding: '0.45rem 0.75rem',
              borderRadius: '6px',
              background: 'rgba(2, 132, 199, 0.06)',
              border: '1px solid rgba(2, 132, 199, 0.2)',
              color: '#0369a1',
              fontSize: '0.74rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Layers size={14} style={{ flexShrink: 0 }} />
            <span>
              <strong>Ámbito operativo diferenciado:</strong> Partidos, jugadas, snaps y evaluaciones técnicas se filtran por la concentración «{currentCampObj?.title || 'seleccionada'}». Los indicadores semanales fuera de campo (carga de entrenamiento RPE, bienestar y cumplimiento de formularios) abarcan {campScope === 'all_active' ? 'a los 22 jugadores activos del equipo' : 'a los atletas convocados a la concentración'} durante el periodo efectivo (<strong>{effectivePeriod.from}</strong> al <strong>{effectivePeriod.to}</strong>).
            </span>
          </div>
        )}
      </div>

      {/* Alerta de error con detalle técnico y reintento funcional */}
      {error && (
        <div
          style={{
            backgroundColor: 'var(--status-danger-bg, #fee2e2)',
            color: 'var(--status-danger-text, #991b1b)',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            fontSize: '0.85rem',
            border: '1px solid #fca5a5',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertCircle size={18} />
              <strong>{error}</strong>
            </div>
            <button
              type="button"
              onClick={loadContext}
              disabled={loading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                borderRadius: '6px',
                background: 'var(--status-danger-text, #991b1b)',
                color: '#ffffff',
                border: 'none',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <RefreshCw size={13} className={loading ? 'spin' : ''} />
              <span>Reintentar</span>
            </button>
          </div>
          {errorDetail && (
            <div style={{ fontSize: '0.74rem', color: '#7f1d1d', opacity: 0.85, fontFamily: 'monospace' }}>
              Diagnóstico técnico: {errorDetail}
            </div>
          )}
        </div>
      )}

      {/* Si el contexto no se ha podido cargar y no hay datos previos, no lanzar subconsultas rotas */}
      {!context && !loading && (
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '2.5rem',
            textAlign: 'center',
            color: 'var(--text-secondary, #64748b)',
          }}
        >
          <AlertCircle size={32} style={{ color: '#ef4444', margin: '0 auto 0.75rem' }} />
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', color: 'var(--text-main, #0f172a)' }}>
            Contexto operativo no disponible
          </h3>
          <p style={{ margin: '0 0 1rem', fontSize: '0.85rem' }}>
            No se pudo sincronizar la información del equipo con el servidor. Comprueba tu conexión y pulsa en «Reintentar».
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={loadContext}
            style={{ fontSize: '0.82rem', padding: '0.45rem 1.1rem' }}
          >
            Reintentar carga de contexto
          </button>
        </div>
      )}

      {/* Renderizado de la experiencia seleccionada (solo si el contexto existe) */}
      {context && activeExperience === 'tracking' && (
        <TeamTrackingView
          teamId={selectedTeamId}
          camp={populationCamp}
          from={effectivePeriod.from}
          to={effectivePeriod.to}
          athletes={context.athletes}
          inactive={showInactive}
          onNavigateToHealth={onNavigateToHealth}
          onNavigateToStats={onNavigateToStats}
          isCoachView={isCoach}
        />
      )}

      {context && activeExperience === 'sport' && (
        <TeamSportPerformanceView
          teamId={selectedTeamId}
          camp={activityCampId}
          from={effectivePeriod.from}
          to={effectivePeriod.to}
          onNavigateToStats={onNavigateToStats}
        />
      )}

      {context && activeExperience === 'individual' && (
        <IndividualPlayerPerformanceView
          teamId={selectedTeamId}
          isAthleteView={isAthlete}
          athletes={context.athletes}
          initialAthleteId={context.athletes?.[0]?.id}
          camp={activityCampId}
          from={effectivePeriod.from}
          to={effectivePeriod.to}
          inactive={showInactive}
          onNavigateToHealth={onNavigateToHealth}
        />
      )}
    </div>
  )
}
export default DashboardView
